// #645 — the ONE visibility/filtering policy for websiteAdsV2 image entries.
//
// Detail galleries, cards, liked ads and the SSR/JSON-LD renderer used to each
// decide for themselves which generation types they would show: the detail
// page ran an exact-match allowlist, everything else ran no filter at all. So
// a new generation type was invisible in one place and leaked unlabelled into
// the others. This module is the single answer to "may this entry be shown,
// and where", and every surface goes through it.
//
// WHY IT LIVES UNDER functions/: firebase.json deploys `functions` as the
// function source root, so a Cloud Function can only require files inside this
// directory — `../src/...` is not in the deployed bundle (the same reason
// webRenderer's `../dist/index.html` read needs an HTTP fallback). The site
// bundle has no such limit, so it imports this file through
// src/services/adImageSource.js, which stays the app's entry point. Plain
// CommonJS with no dependencies so both loaders take it as-is.
//
// Generation types reach us from the platform pipeline via websiteSync
// (`generation_type` -> `type`, `generation_style` -> `style`):
//   furnish / furnishStyle  final furnished renders — the ad's selling point
//   nakedRenewedStyle       renovated, no furniture, PER STYLE — the FINAL
//                           visible renovation result
//   nakedRenewed            the INTERNAL NEUTRAL renovation result: shown only
//                           where no usable styled one exists for that room
//   nakedInteriors          the emptied real photo (an intermediate step)
//   anything else           an intermediate, or a type this build predates
//
// An entry with NO type is a migrated `newarcHome` ad's photo: websiteSync's
// collectNewarcHomeGalleries writes `type: null` on every project entry, and
// those are real user-facing images. Untyped therefore means LEGACY, never
// "unknown" — reading it as unknown would blank every migrated ad's card.

/**
 * Final furnished renders — BOTH deliberately public.
 *
 * NON-GOAL (#645): do NOT give these the styled-first treatment the renovation
 * types get below. `furnish` is not an intermediate: websiteSync forwards it on
 * purpose even when Tiresia flags it hidden ("`furnish` is a real final image",
 * websiteSync.ts:347), and the detail page uses it as the BASE gallery — the
 * default render a style falls back to on rooms it does not cover. The
 * neutral-renovation rule is about an internal artefact; this is a published
 * default. Keeping them symmetrical would be the mistake.
 * @const {!Array<string>}
 */
const FURNISHED_TYPES = ["furnish", "furnishStyle"];

/**
 * The FINAL visible renovation result: renovated, unfurnished, per style.
 * @const {string}
 */
const RENOVATED_STYLED_TYPE = "nakedRenewedStyle";

/**
 * The INTERNAL NEUTRAL renovation result. Not a display output: it is shown
 * only where no usable styled result exists for that room. Tiresia is moving to
 * persisting it as hidden, but the site cannot wait for that — old and stale
 * synchronized records still carry both, so styled-first is enforced here.
 *
 * The styled result itself is never at risk: Tiresia guarantees
 * `nakedRenewedStyle` is written with hide_it:false, so the sync always
 * forwards it and the hide_it check below is defensive only.
 * @const {string}
 */
const RENOVATED_NEUTRAL_TYPE = "nakedRenewed";

/**
 * Both renovation results, neutral first. Membership of this class says an
 * entry is a renovation render — NOT that it may be displayed; that is decided
 * hierarchically, see cardEligibleEntries.
 * @const {!Array<string>}
 */
const RENOVATED_EMPTY_TYPES = [RENOVATED_NEUTRAL_TYPE, RENOVATED_STYLED_TYPE];

/**
 * Normalized generation type of an entry.
 * @param {?Object} entry a websiteAdsV2 gallery entry.
 * @return {string} the type, or "" for a legacy/untyped entry.
 */
function generationType(entry) {
  if (!entry || typeof entry !== "object") return "";
  const t = entry.type;
  return typeof t === "string" ? t : "";
}

/**
 * Is this a migrated newarcHome photo (no generation type at all)?
 * @param {?Object} entry a websiteAdsV2 gallery entry.
 * @return {boolean} true when the entry carries no type.
 */
function isLegacyUntyped(entry) {
  return generationType(entry) === "";
}

/**
 * Is this one of the final furnished renders?
 * @param {?Object} entry a websiteAdsV2 gallery entry.
 * @return {boolean} true for furnish / furnishStyle.
 */
function isFurnishedType(entry) {
  return FURNISHED_TYPES.indexOf(generationType(entry)) !== -1;
}

/**
 * Is this one of the renovated-bare renders?
 * @param {?Object} entry a websiteAdsV2 gallery entry.
 * @return {boolean} true for nakedRenewed / nakedRenewedStyle.
 */
function isRenovatedEmptyType(entry) {
  return RENOVATED_EMPTY_TYPES.indexOf(generationType(entry)) !== -1;
}

/**
 * The room an entry belongs to, or null when it names no room.
 *
 * Null is "unknown room", NOT a room of its own: every untagged entry is its
 * own island, so per-room rules can neither merge them together nor apply one
 * entry's outcome to another.
 * @param {?Object} entry a websiteAdsV2 gallery entry.
 * @return {?string} the room tag, or null when there is none.
 */
function roomKey(entry) {
  if (!entry || typeof entry !== "object") return null;
  const tag = entry.tag;
  return typeof tag === "string" && tag !== "" ? tag : null;
}

/**
 * Explicitly hidden by the pipeline.
 *
 * Defensive: websiteSync already drops `hide_it === true` server-side and does
 * not forward the field (see its collectGalleries), so this is a no-op against
 * today's payload. It is here so that the day the field IS forwarded, every
 * surface honours it at once instead of one of them publishing a hidden image.
 * Only an explicit `true` hides — an absent flag is not a hidden image.
 * @param {?Object} entry a websiteAdsV2 gallery entry.
 * @return {boolean} true when the entry is flagged hidden.
 */
function isHiddenEntry(entry) {
  if (!entry || typeof entry !== "object") return false;
  return entry.hide_it === true;
}

/**
 * May this entry be rendered at all? An empty `url` is a still-processing
 * immaginaCasa image: it self-heals on the next sync, so it is skipped rather
 * than rendered broken.
 * @param {?Object} entry a websiteAdsV2 gallery entry.
 * @return {boolean} true when the entry may be shown.
 */
function isVisibleEntry(entry) {
  if (!entry || typeof entry !== "object") return false;
  if (!entry.url) return false;
  return !isHiddenEntry(entry);
}

/**
 * The project-gallery entries a CARD may show — also the set the SSR renderer
 * puts in JSON-LD, so a crawler and a visitor see the same images.
 *
 * Two tiers, because a card advertises the ad's best final output:
 *   1. furnished renders, plus legacy untyped photos (they never co-occur, and
 *      a migrated ad has nothing else);
 *   2. failing that, the renovation renders, resolved styled-first PER ROOM;
 *   3. failing that, nothing: intermediates (nakedInteriors,
 *      perspectiveCorrection) and unknown future types never reach a card, and
 *      the caller falls back to the real photos.
 *
 * Tier 2 is decided room by room, NOT for the gallery as a whole. Generation is
 * incremental, so a partially generated ad has one room already styled while
 * another still carries only the neutral render; suppressing the neutral
 * globally would drop that second room from the card altogether. So: a room
 * loses its neutral render only when that same room has at least one usable
 * styled render. Every styled branch survives — one per room per style.
 *
 * Untagged entries (`tag` null/empty — every migrated newarcHome photo, and any
 * malformed record) belong to no known room. They are handled conservatively:
 * an untagged entry is never suppressed by another room's styled render, and
 * never suppresses anyone else's. Collapsing them into one pseudo-room would
 * merge unrelated images and silently delete legacy ones.
 *
 * Source order is preserved throughout, so the card slider keeps the gallery's
 * own ordering.
 *
 * Tier 3 is what keeps cards and the detail page consistent: an unrecognized
 * type is now dropped by BOTH, instead of being dropped by one and published
 * by the other.
 * @param {?Array<Object>} entries a project gallery.
 * @return {!Array<Object>} the entries a card may show, in order.
 */
function cardEligibleEntries(entries) {
  if (!Array.isArray(entries)) return [];
  const visible = entries.filter(isVisibleEntry);
  const furnished = visible.filter(
      (e) => isFurnishedType(e) || isLegacyUntyped(e),
  );
  if (furnished.length > 0) return furnished;

  // Rooms that already have a usable styled render — the only rooms whose
  // neutral render is redundant. Untagged entries contribute no room.
  // A Set avoids treating user-controlled room labels such as "__proto__" as
  // object properties. Room labels are opaque identities here, not keys in a
  // data model.
  const styledRooms = new Set();
  visible.forEach((e) => {
    if (generationType(e) !== RENOVATED_STYLED_TYPE) return;
    const room = roomKey(e);
    if (room !== null) styledRooms.add(room);
  });

  return visible.filter((e) => {
    const type = generationType(e);
    if (type === RENOVATED_STYLED_TYPE) return true;
    if (type !== RENOVATED_NEUTRAL_TYPE) return false;
    const room = roomKey(e);
    return room === null || !styledRooms.has(room);
  });
}

module.exports = {
  FURNISHED_TYPES,
  RENOVATED_EMPTY_TYPES,
  RENOVATED_STYLED_TYPE,
  RENOVATED_NEUTRAL_TYPE,
  generationType,
  isLegacyUntyped,
  isFurnishedType,
  isRenovatedEmptyType,
  isHiddenEntry,
  isVisibleEntry,
  roomKey,
  cardEligibleEntries,
};
